import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { useDropzone } from "react-dropzone";
import { api } from "../lib/api";
import { useAccounts } from "../lib/account";
import { accountCurrency } from "../lib/currency";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, Info, Lightning } from "@phosphor-icons/react";

/**
 * One-click MT4/MT5 statement import.
 *
 * The user saves their MetaTrader account history as an HTML statement and
 * drops it here. We parse, dedupe, and insert — no column mapping, no CSV
 * rearranging. Works with any MT4/MT5 broker or prop firm.
 */

const PLATFORM_STEPS = {
  mt5: [
    "In MetaTrader 5, open the Toolbox → History tab.",
    "Right-click anywhere in the history → Report → Open XML/HTML.",
    "Save the .html file to your desktop.",
    "Drop that file into the box above — done.",
  ],
  mt4: [
    "In MetaTrader 4, open the Terminal → Account History tab.",
    "Right-click anywhere → Save as Detailed Report.",
    "Save the .htm file to your desktop.",
    "Drop that file into the box above — done.",
  ],
};

const SUPPORTED_TARGETS = [
  { name: "FTMO",           platform: "MT4/MT5" },
  { name: "MyForexFunds",   platform: "MT4/MT5" },
  { name: "FundedNext",     platform: "MT4/MT5" },
  { name: "The5ers",        platform: "MT4/MT5" },
  { name: "E8 Funding",     platform: "MT4/MT5" },
  { name: "TFT",            platform: "MT4/MT5" },
  { name: "Fidelcrest",     platform: "MT4/MT5" },
  { name: "Blue Guardian",  platform: "MT4/MT5" },
  { name: "Exness",         platform: "MT4/MT5" },
  { name: "IC Markets",     platform: "MT4/MT5" },
  { name: "XM",             platform: "MT4/MT5" },
  { name: "Pepperstone",    platform: "MT4/MT5" },
  { name: "OANDA",          platform: "MT4" },
  { name: "Hantec",         platform: "MT4/MT5" },
];

export default function MtStatementImport({ open, onOpenChange, onImported, defaultPlatform = "mt5" }) {
  const { accounts, activeId, reload, setActive } = useAccounts();
  const [platform, setPlatform] = useState(defaultPlatform);
  const [selectedAccount, setSelectedAccount] = useState(activeId);
  const [autoCreate, setAutoCreate] = useState(true);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) { setFile(null); setResult(null); setProgress(0); }
    else if (activeId && !selectedAccount) setSelectedAccount(activeId);
  }, [open, activeId, selectedAccount]);

  useEffect(() => { setPlatform(defaultPlatform); }, [defaultPlatform]);

  const targetAcc = useMemo(
    () => accounts.find(a => a.id === selectedAccount) || null,
    [accounts, selectedAccount],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "text/html": [".html", ".htm"],
      "application/xhtml+xml": [".xhtml"],
    },
    maxFiles: 1,
    onDrop: useCallback((files) => {
      setResult(null);
      setProgress(0);
      setFile(files[0]);
    }, []),
  });

  const doImport = async () => {
    if (!file) return toast.error("Drop your MT4/MT5 statement file first");
    setLoading(true);
    setResult(null);
    setProgress(0);

    const params = new URLSearchParams();
    if (autoCreate) {
      params.set("create_new_account", "true");
    } else if (selectedAccount || activeId) {
      params.set("account_id", selectedAccount || activeId);
    }

    const sizeKb = (file.size / 1024).toFixed(1);
    const toastId = toast.loading(`Parsing ${file.name} (${sizeKb} KB)…`, {
      description: "Reading trades from your MetaTrader statement",
    });

    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/mt/statement/upload?${params.toString()}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
          onUploadProgress: (e) => {
            if (e.total) {
              const p = Math.round((e.loaded / e.total) * 100);
              setProgress(p);
              // Update the loading toast so users see something moving on big files
              if (p < 100) {
                toast.loading(`Uploading… ${p}%`, { id: toastId });
              } else {
                toast.loading("Parsing trades on the server…", { id: toastId });
              }
            }
          },
        },
      );
      setResult(data);
      toast.success(`${data.inserted} new trades imported`, {
        id: toastId,
        description: [
          data.platform && `Detected: ${data.platform.toUpperCase()}`,
          data.broker && data.broker !== "MetaTrader" && data.broker,
          data.account_created && "New account created",
          data.duplicates && `${data.duplicates} duplicates skipped`,
        ].filter(Boolean).join(" · "),
      });
      if (data.account_id) {
        await reload();
        setActive?.(data.account_id);
      }
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto text-foreground" data-testid="mt-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            One-click MT4 / MT5 Import
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Drop your MetaTrader account statement — we auto-detect the platform, broker, and every closed trade. No column mapping, no re-arranging.
          </p>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Platform</Label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="mt-import-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mt5" data-testid="mt-import-platform-mt5">MetaTrader 5</SelectItem>
                <SelectItem value="mt4" data-testid="mt-import-platform-mt4">MetaTrader 4</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={autoCreate ? "opacity-50" : ""}>Import into account</Label>
            <Select
              value={selectedAccount || ""}
              onValueChange={setSelectedAccount}
              disabled={autoCreate}>
              <SelectTrigger data-testid="mt-import-account"><SelectValue placeholder="Select account" /></SelectTrigger>
              <SelectContent>
                {accounts.map(a => (
                  <SelectItem key={a.id} value={a.id} data-testid={`mt-import-acc-${a.id}`}>
                    {a.name} · {accountCurrency(a)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <label className="flex items-start gap-3 rounded-lg border border-border/60 bg-secondary/40 p-3 cursor-pointer" data-testid="mt-import-autocreate-row">
          <Switch
            checked={autoCreate}
            onCheckedChange={setAutoCreate}
            data-testid="mt-import-autocreate-switch"
            className="mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium flex items-center gap-1.5">
              <Lightning size={13} weight="fill" className="text-primary" />
              Auto-create a new account named after the broker
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {autoCreate
                ? "We'll create (or reuse) an account like \"FTMO (MT5)\" so this statement stays separate from your other accounts."
                : "Trades will be added into the selected account above."}
            </p>
          </div>
        </label>

        <div {...getRootProps()}
          data-testid="mt-statement-dropzone"
          className={`mt-2 border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${isDragActive ? "border-primary bg-primary/10 scale-[1.01]" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}>
          <input {...getInputProps()} data-testid="mt-statement-file-input" />
          <UploadSimple size={32} className="mx-auto text-muted-foreground mb-2" />
          {file ? (
            <>
              <p className="text-sm font-medium" data-testid="mt-statement-filename">{file.name}</p>
              <p className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB — ready to import</p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">Drop your MT4/MT5 statement here</p>
              <p className="text-xs text-muted-foreground mt-1">Supports .html and .htm — works for FTMO, MyForexFunds, Exness, IC Markets, XM, Pepperstone, and every MT4/MT5 broker.</p>
            </>
          )}
        </div>

        {loading && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="mt-import-progress">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-primary font-medium">
                {progress < 100 ? `Uploading ${file?.name || "statement"}…` : "Parsing trades on the server…"}
              </span>
              <span className="font-mono text-primary/80">{progress}%</span>
            </div>
            <div className="h-1.5 bg-primary/15 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-200 ease-out"
                style={{ width: `${progress || 5}%` }} />
            </div>
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs flex items-start gap-2" data-testid="mt-import-result">
            <CheckCircle size={16} className="text-emerald-500 shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                Imported {result.inserted} trades from {(result.platform || "").toUpperCase()} statement
              </p>
              <p className="text-muted-foreground">
                {result.duplicates ? `${result.duplicates} duplicates skipped · ` : ""}
                {result.parsed_total} trades found in file
                {result.broker && ` · Broker: ${result.broker}`}
                {result.mt_account && ` · MT account: ${result.mt_account}`}
                {result.account_created && " · Auto-created new account"}
              </p>
            </div>
          </div>
        )}

        <details className="rounded-lg border border-border/60 bg-secondary/40 p-3 text-xs" data-testid="mt-import-howto">
          <summary className="cursor-pointer font-semibold text-sm flex items-center gap-1.5">
            <Info size={14} /> How to save your {platform.toUpperCase()} statement (30 seconds)
          </summary>
          <ol className="mt-3 space-y-1.5 list-decimal list-inside text-muted-foreground">
            {(PLATFORM_STEPS[platform] || PLATFORM_STEPS.mt5).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </details>

        <details className="rounded-lg border border-border/60 bg-secondary/40 p-3 text-xs" data-testid="mt-import-supported">
          <summary className="cursor-pointer font-semibold text-sm">
            Verified prop firms &amp; brokers (all MT4/MT5 statements)
          </summary>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-1.5 text-muted-foreground">
            {SUPPORTED_TARGETS.map(t => (
              <div key={t.name} className="flex items-center gap-1.5">
                <CheckCircle size={12} className="text-emerald-500 shrink-0" />
                <span>{t.name} <span className="text-[10px] opacity-60">· {t.platform}</span></span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Anything else running on MT4 or MT5 works too — the parser reads the standard MetaTrader HTML statement.
          </p>
        </details>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mt-import-cancel">
            Close
          </Button>
          <Button
            onClick={doImport}
            disabled={loading || !file}
            className="rounded-full"
            data-testid="mt-import-run">
            {loading ? "Importing…" : (result ? "Re-import" : "Import trades")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
